import type { StudioModule, StudioModuleCatalog, StudioModuleChange } from '@/api/studioModules';
import { MANAGE_MODULES_KEY, destinationByKey } from '@/layouts/navigation';

// Pure helpers for Administration → Modules: what a proposed save will do (impact),
// what a history row changed (diff), and how to merge an editor's work with someone
// else's newer save (mergeModule). The SERVER validates and decides; these only
// explain, so the editor can warn before it asks.

export interface DraftItem {
  navKey: string;
  /** The placement row's version when the editor loaded it, or null when it was placed nowhere. */
  rowVersion: string | null;
  /** The module that owned it when loaded (null = placed nowhere). */
  fromModuleKey: string | null;
}

export interface ModuleDraft {
  moduleKey: string;
  title: string;
  description: string;
  iconKey: string;
  color: string;
  isVisible: boolean;
  defaultNavKey: string | null;
  items: DraftItem[];
}

export const titleOf = (navKey: string, catalog?: StudioModuleCatalog | null): string =>
  destinationByKey(navKey)?.label ?? catalog?.destinations.find((d) => d.navKey === navKey)?.title ?? navKey;

const moduleTitle = (key: string | null | undefined, catalog: StudioModuleCatalog): string =>
  catalog.modules.find((m) => m.moduleKey === key)?.title ?? key ?? 'no module';

/** Turns a title into a module key the server accepts: lowercase letters, digits and single hyphens, starting with a letter. */
export function slugify(title: string): string {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
  return slug;
}

export function draftFrom(module: StudioModule | null): ModuleDraft {
  if (!module) {
    return { moduleKey: '', title: '', description: '', iconKey: 'folder', color: '#64748B', isVisible: true, defaultNavKey: null, items: [] };
  }
  return {
    moduleKey: module.moduleKey,
    title: module.title,
    description: module.description ?? '',
    iconKey: module.iconKey,
    color: module.color ?? '',
    isVisible: module.isVisible,
    defaultNavKey: module.defaultNavKey ?? null,
    // Destinations this build does not know stay where they are on the server; the editor never sends them.
    items: module.items.filter((i) => i.known).map((i) => ({ navKey: i.navKey, rowVersion: i.rowVersion ?? null, fromModuleKey: module.moduleKey })),
  };
}

/** Plain-language consequences of saving the draft, for the impact summary. */
export function impactOf(draft: ModuleDraft, original: StudioModule | null, catalog: StudioModuleCatalog): string[] {
  const lines: string[] = [];
  const selfKey = original?.moduleKey ?? null;

  for (const item of draft.items) {
    if (item.fromModuleKey === selfKey && selfKey !== null) continue;
    const title = titleOf(item.navKey, catalog);
    if (item.fromModuleKey == null) {
      lines.push(`${title} joins this module (it was in no module).`);
      continue;
    }
    const from = catalog.modules.find((m) => m.moduleKey === item.fromModuleKey);
    const lastOne = from ? from.items.filter((i) => i.known).length === draft.items.filter((d) => d.fromModuleKey === from.moduleKey).length : false;
    let line = `${title} moves here from ${moduleTitle(item.fromModuleKey, catalog)}. Its route, data scope and who may open it do not change.`;
    if (from?.defaultNavKey === item.navKey) line += ` It is ${from.title}'s default, so ${from.title} will open on its first page instead.`;
    if (lastOne) line += ` ${from!.title} will have no pages left and will drop out of the launcher until it gets one.`;
    lines.push(line);
  }

  if (!draft.isVisible) {
    lines.push('Hidden: the tile, the menu, search results and pins for this module disappear for everyone. Its pages still open from a bookmark for anyone allowed to use them — hiding is not access control.');
  }
  if (draft.items.length === 0) {
    lines.push('Draft: with no pages it is not shown in the launcher. Add a page to make it launchable.');
  }
  if (draft.items.length > 0 && !draft.defaultNavKey) {
    lines.push(`Opens on the first page each person can open (${titleOf(draft.items[0].navKey, catalog)} for most).`);
  }
  if (original && original.title !== draft.title.trim()) {
    lines.push(`Renamed from "${original.title}". Its key "${original.moduleKey}" and every page's URL stay the same.`);
  }
  return lines;
}

/** True when the module would hold Manage modules — the recovery route, which must stay shown. */
export const holdsRecoveryRoute = (draft: ModuleDraft): boolean => draft.items.some((i) => i.navKey === MANAGE_MODULES_KEY);

interface Snapshot {
  Title?: string;
  Description?: string | null;
  IconKey?: string;
  Color?: string | null;
  DisplayOrder?: number;
  DefaultNavKey?: string | null;
  IsVisible?: boolean;
  Items?: string[];
}

const parse = (json?: string | null): Snapshot | null => {
  if (!json) return null;
  try {
    return JSON.parse(json) as Snapshot;
  } catch {
    return null;
  }
};

/** One history row in words: which fields changed and which pages came or went. */
export function describeChange(change: StudioModuleChange, catalog?: StudioModuleCatalog | null): string[] {
  const before = parse(change.beforeJson);
  const after = parse(change.afterJson);
  switch (change.action) {
    case 'seeded':
      return ['Created by the initial catalog seed.'];
    case 'created':
      return [`Created with ${after?.Items?.length ?? 0} page(s)${after?.Items?.length ? `: ${after.Items.map((k) => titleOf(k, catalog)).join(', ')}` : ''}.`];
    case 'deleted':
      return [`Removed (it held no pages). Last name: "${before?.Title ?? change.moduleKey}".`];
    case 'reordered':
      return [`Moved in the launcher from position ${(before?.DisplayOrder ?? 0) + 1} to ${(after?.DisplayOrder ?? 0) + 1}.`];
    default:
      break;
  }
  if (!before || !after) return ['Changed.'];
  const lines: string[] = [];
  const field = (label: string, a: unknown, b: unknown) => {
    if ((a ?? '') !== (b ?? '')) lines.push(`${label}: "${a ?? '—'}" → "${b ?? '—'}"`);
  };
  field('Name', before.Title, after.Title);
  field('Description', before.Description, after.Description);
  field('Icon', before.IconKey, after.IconKey);
  field('Color', before.Color, after.Color);
  if (before.IsVisible !== after.IsVisible) lines.push(after.IsVisible ? 'Shown in navigation again' : 'Hidden from navigation');
  field('Default page', before.DefaultNavKey ? titleOf(before.DefaultNavKey, catalog) : 'first page', after.DefaultNavKey ? titleOf(after.DefaultNavKey, catalog) : 'first page');
  const b = before.Items ?? [];
  const a = after.Items ?? [];
  const added = a.filter((k) => !b.includes(k));
  const removed = b.filter((k) => !a.includes(k));
  if (added.length) lines.push(`Gained: ${added.map((k) => titleOf(k, catalog)).join(', ')}`);
  if (removed.length) lines.push(`${change.action === 'moved-out' ? 'Gave to another module' : 'Lost'}: ${removed.map((k) => titleOf(k, catalog)).join(', ')}`);
  if (!added.length && !removed.length && a.join('|') !== b.join('|')) lines.push('Pages reordered');
  return lines.length ? lines : ['Saved with no visible change.'];
}

// ── Three-way merge after a conflict ─────────────────────────────────────────
// ORIGINAL = the module as this editor loaded it, YOURS = the editor's draft, CURRENT = the module (and catalog) as saved
// now. A field only one side changed takes that side; a field both changed differently is a conflict the person must
// decide. A page someone else moved away stays moved; a page this editor is taking that someone else moved meanwhile is a
// conflict. Versions come from CURRENT only after intent is settled, so a save can never carry a fresh version for a
// change nobody chose.

export type MergeField = 'title' | 'description' | 'iconKey' | 'color' | 'isVisible' | 'defaultNavKey';
export type MergeChoice = 'yours' | 'current';

export interface MergeConflict {
  /** Stable across re-merges, so a choice keyed by it survives: `field:title`, `order`, `page:workflows`. */
  id: string;
  kind: 'field' | 'order' | 'page';
  label: string;
  original: string;
  yours: string;
  current: string;
}

export interface ModuleMerge {
  /** Ready to save once `unresolved` is empty; until then every undecided conflict holds CURRENT. */
  draft: ModuleDraft;
  conflicts: MergeConflict[];
  unresolved: MergeConflict[];
  /** What was taken from CURRENT without asking, in words. */
  notes: string[];
}

const MERGE_FIELDS: Array<[MergeField, string]> = [
  ['title', 'Name'],
  ['description', 'Description'],
  ['iconKey', 'Icon'],
  ['color', 'Color'],
  ['isVisible', 'Shown in navigation'],
  ['defaultNavKey', 'Opens on'],
];

const shownValue = (field: MergeField, value: ModuleDraft[MergeField], catalog: StudioModuleCatalog): string => {
  if (field === 'isVisible') return value ? 'Shown' : 'Hidden';
  if (field === 'defaultNavKey') return value ? titleOf(String(value), catalog) : 'First page';
  return value === '' || value == null ? '—' : String(value);
};

const sameOrder = (a: string[], b: string[]): boolean => a.length === b.length && a.every((k, i) => k === b[i]);

/** `primary` in its own order; each key only `secondary` has goes right after its nearest predecessor there. */
function weave(primary: string[], secondary: string[]): string[] {
  const out = [...primary];
  secondary.forEach((key, index) => {
    if (out.includes(key)) return;
    let at = 0;
    for (let i = index - 1; i >= 0; i--) {
      const before = out.indexOf(secondary[i]);
      if (before >= 0) {
        at = before + 1;
        break;
      }
    }
    out.splice(at, 0, key);
  });
  return out;
}

/**
 * Merges ORIGINAL, YOURS and CURRENT (`original`/`latest` are null when creating). Call again with `choices` as the
 * person decides; the result's `unresolved` must be empty before the draft is saved.
 */
export function mergeModule(
  original: StudioModule | null,
  yours: ModuleDraft,
  latest: StudioModule | null,
  catalog: StudioModuleCatalog,
  choices: Record<string, MergeChoice> = {},
): ModuleMerge {
  const conflicts: MergeConflict[] = [];
  const notes: string[] = [];
  const o = original ? draftFrom(original) : null;
  const t = latest ? draftFrom(latest) : null;
  const draft: ModuleDraft = { ...yours };
  const title = (navKey: string) => titleOf(navKey, catalog);

  // Fields.
  if (o && t) {
    for (const [field, label] of MERGE_FIELDS) {
      const before = o[field];
      const mine = yours[field];
      const now = t[field];
      if (mine === before || mine === now) {
        (draft[field] as ModuleDraft[MergeField]) = now;
        if (mine === before && now !== before) notes.push(`${label} is now "${shownValue(field, now, catalog)}" — someone else's change, kept.`);
        continue;
      }
      if (now === before) continue; // only yours changed
      const id = `field:${field}`;
      conflicts.push({ id, kind: 'field', label, original: shownValue(field, before, catalog), yours: shownValue(field, mine, catalog), current: shownValue(field, now, catalog) });
      (draft[field] as ModuleDraft[MergeField]) = choices[id] === 'yours' ? mine : now;
    }
  }

  // Which pages the module holds.
  const O = o?.items.map((i) => i.navKey) ?? [];
  const T = t?.items.map((i) => i.navKey) ?? [];
  const L = yours.items.map((i) => i.navKey);
  const destination = new Map(catalog.destinations.map((d) => [d.navKey, d]));
  const keep = new Set(T); // a page leaves a module only by joining another, so everything it holds now stays

  for (const k of T) if (!O.includes(k) && !L.includes(k)) notes.push(`${title(k)} was added to this module by someone else; it stays.`);
  for (const k of O) {
    if (T.includes(k)) continue;
    const owner = destination.get(k)?.ownerModuleKey ?? null;
    notes.push(`${title(k)} was moved to ${moduleTitle(owner, catalog)} by someone else; it stays there.`);
  }
  for (const item of yours.items) {
    const k = item.navKey;
    if (O.includes(k) || T.includes(k)) continue;
    const owner = destination.get(k)?.ownerModuleKey ?? null;
    if (owner === item.fromModuleKey) {
      keep.add(k); // still where you took it from: your move goes ahead
      continue;
    }
    const id = `page:${k}`;
    conflicts.push({ id, kind: 'page', label: title(k), original: `In ${moduleTitle(item.fromModuleKey, catalog)}`, yours: 'Move it here', current: `In ${moduleTitle(owner, catalog)}` });
    if (choices[id] === 'yours') keep.add(k);
  }

  // Their order. Compared on the pages every side holds; a reorder only one side made wins.
  const common = O.filter((k) => T.includes(k) && L.includes(k));
  const mineOrder = L.filter((k) => common.includes(k));
  const nowOrder = T.filter((k) => common.includes(k));
  let primary: 'yours' | 'current' = 'current';
  if (!sameOrder(mineOrder, common)) {
    if (sameOrder(nowOrder, common) || sameOrder(nowOrder, mineOrder)) primary = 'yours';
    else {
      const id = 'order';
      const words = (keys: string[]) => keys.map(title).join(', ');
      conflicts.push({ id, kind: 'order', label: 'Page order', original: words(common), yours: words(mineOrder), current: words(nowOrder) });
      primary = choices[id] === 'yours' ? 'yours' : 'current';
    }
  }
  const order = primary === 'yours' ? weave(L.filter((k) => keep.has(k)), T) : weave(T, L.filter((k) => keep.has(k)));

  // Versions last, from CURRENT, now that every page's place is decided.
  const latestItem = new Map((latest?.items ?? []).map((i) => [i.navKey, i]));
  draft.items = order.map<DraftItem>((navKey) =>
    latestItem.has(navKey)
      ? { navKey, rowVersion: latestItem.get(navKey)!.rowVersion ?? null, fromModuleKey: latest!.moduleKey }
      : { navKey, rowVersion: destination.get(navKey)?.itemRowVersion ?? null, fromModuleKey: destination.get(navKey)?.ownerModuleKey ?? null },
  );
  if (draft.defaultNavKey && !draft.items.some((i) => i.navKey === draft.defaultNavKey)) {
    notes.push(`It opened on ${title(draft.defaultNavKey)}, which is no longer in this module; it now opens on the first page.`);
    draft.defaultNavKey = null;
  }

  return { draft, conflicts, unresolved: conflicts.filter((c) => !choices[c.id]), notes };
}
